import { useCallback, useEffect, useRef, useState } from 'react';

import { track } from '@/lib/mixpanel';

interface DismissalServiceSnapshot {
  providerName: string;
  active: boolean;
}

interface ImportStatusForDismissal {
  hasActiveImports: boolean;
  isLoading: boolean;
  combinedProgress: number;
  /**
   * Provider-agnostic services list. The hook only needs `providerName` +
   * `active` for telemetry; passing the full `ServiceImportStatus` works
   * because the extra fields are ignored.
   */
  services: DismissalServiceSnapshot[];
}

interface UseOnboardingDismissalOptions {
  /** Organization ID for scoping the dismissal */
  orgId: string;
  /** User ID for scoping the dismissal (from useAuthInfo) */
  userId: number | undefined;
  /** Import status from useImportStatus hook */
  importStatus: ImportStatusForDismissal;
  /** Unique key prefix to differentiate between different usages */
  storageKeyPrefix: string;
  /** Analytics event name for dismissal tracking */
  dismissEventName: string;
  /**
   * When true, dismissal state clears automatically when imports complete for the first time
   * (user saw active imports). When false, dismissal persists until user explicitly dismisses.
   * Default: false
   */
  clearOnFirstCompletion?: boolean;
  /**
   * Whether to track shown/completed events for the full onboarding lifecycle.
   * Default: false
   */
  trackFullLifecycle?: boolean;
  /**
   * Optional callback invoked after dismissal state is set.
   * Can be used for navigation or other side effects.
   */
  onAfterDismiss?: () => void;
}

interface UseOnboardingDismissalReturn {
  /** Whether the onboarding should be shown (active imports AND not dismissed) */
  showOnboarding: boolean;
  /** Whether the user has dismissed the onboarding */
  isDismissed: boolean;
  /** Handler to dismiss the onboarding. Call this from the dismiss button. */
  handleDismiss: () => void;
}

/**
 * Hook for managing onboarding dismissal state during Gmail/Calendar imports.
 *
 * **IMPORTANT: This hook has TWO clearing behaviors controlled by `clearOnFirstCompletion`:**
 * - `true`: Clears when imports finish AND user saw active imports (AIActionsPanel)
 * - `false`: Clears when imports finish AND user had dismissed (ProposalCenter)
 *
 * This hook manages 5 distinct concerns:
 * 1. **Dismissal state** - Persists user's dismissal choice to localStorage per org/user
 * 2. **hasSeenActive tracking** - Tracks if user has ever seen active imports (optional)
 * 3. **State clearing** - Automatically clears dismissal when imports complete
 * 4. **Analytics events** - Fires dismissal and lifecycle events to Mixpanel
 * 5. **Callbacks** - Executes optional callback after dismissal (e.g., navigation)
 *
 * ## Configuration Modes
 *
 * The `clearOnFirstCompletion` option controls the state clearing behavior:
 *
 * **`clearOnFirstCompletion: true`** (AIActionsPanel behavior)
 * - Clears state when: imports finish AND user previously saw active imports
 * - Use case: Panel that shows ONLY during imports. Clearing ensures fresh state
 *   for next import session, but only if user actually saw this one.
 *
 * **`clearOnFirstCompletion: false`** (ProposalCenter behavior)
 * - Clears state when: imports finish AND user had dismissed the banner
 * - Use case: Banner within a larger page. Clearing resets the dismissal so
 *   user sees the banner again on next import session.
 *
 * @example
 * // AIActionsPanel: clear on first completion, navigate on dismiss
 * const { showOnboarding, handleDismiss } = useOnboardingDismissal({
 *   orgId, userId, importStatus,
 *   storageKeyPrefix: 'ai_actions_onboarding',
 *   dismissEventName: 'ai_actions_onboarding_dismissed',
 *   clearOnFirstCompletion: true,
 *   trackFullLifecycle: true,
 *   onAfterDismiss: () => navigate('/proposals'),
 * });
 *
 * @example
 * // ProposalCenter: simpler banner dismissal
 * const { showOnboarding, handleDismiss } = useOnboardingDismissal({
 *   orgId, userId, importStatus,
 *   storageKeyPrefix: 'onboarding_import_dismissed',
 *   dismissEventName: 'onboarding_import_banner_dismissed',
 * });
 */
export function useOnboardingDismissal(
  options: UseOnboardingDismissalOptions
): UseOnboardingDismissalReturn {
  const {
    orgId,
    userId,
    importStatus,
    storageKeyPrefix,
    dismissEventName,
    // Default false for simpler banner use case (ProposalCenter).
    // Set true for dedicated panels (AIActionsPanel) that need to clear on first completion.
    clearOnFirstCompletion = false,
    // Default false to avoid analytics events for simple dismissal.
    // Set true to track shown/completed lifecycle events (e.g., onboarding_import_shown).
    trackFullLifecycle = false,
    onAfterDismiss,
  } = options;

  // Generate storage keys.
  // Callers can safely pass undefined userId (e.g., during auth loading).
  // The hook will not show onboarding (showOnboarding=false) until userId is defined.
  // See line 248: showOnboarding requires userId !== undefined.
  //
  // Use 'anonymous' fallback to clearly indicate the key is not user-specific.
  // Warn in development if fallback is used, as it may cause issues with shared browsers.
  const userIdFallback = 'anonymous';
  if (userId === undefined && process.env.NODE_ENV !== 'production') {
    console.warn(
      'useOnboardingDismissal: userId is undefined, using anonymous fallback. ' +
        'This may cause unexpected behavior if multiple users share the same browser.'
    );
  }
  const dismissalKey = `${storageKeyPrefix}_${orgId}_${userId ?? userIdFallback}`;
  const hasSeenActiveKey = clearOnFirstCompletion
    ? `${storageKeyPrefix}_has_seen_active_${orgId}_${userId ?? userIdFallback}`
    : null;

  // State initialization from localStorage (with error handling)
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(dismissalKey) === 'true';
    } catch {
      return false;
    }
  });

  const [hasSeenActive, setHasSeenActive] = useState<boolean>(() => {
    if (!clearOnFirstCompletion || !hasSeenActiveKey) {
      return false;
    }
    try {
      return localStorage.getItem(hasSeenActiveKey) === 'true';
    } catch {
      return false;
    }
  });

  // Tracking refs for lifecycle events
  const onboardingStartTimeRef = useRef<number | null>(null);
  const hasTrackedShownRef = useRef(false);
  const hasTrackedCompletedRef = useRef(false);
  // Track previous active state to detect new import sessions
  const prevHasActiveImportsRef = useRef(importStatus.hasActiveImports);
  // Track if state is synced with the current dismissalKey to prevent stale state issues.
  // Initialize with the current dismissalKey so the first render has correct state.
  const syncedKeyRef = useRef<string>(dismissalKey);
  const isStateSynced = syncedKeyRef.current === dismissalKey;

  /**
   * Reset state when org or user context changes.
   *
   * This ensures each org/user has independent dismissal state—a user switching
   * orgs will see the onboarding UI if that org has active imports, regardless
   * of what they dismissed in another org. The hook re-reads from localStorage
   * with the new key (which includes orgId and userId).
   *
   * This is intentional behavior for multi-org and multi-user support.
   */
  useEffect(() => {
    try {
      setIsDismissed(localStorage.getItem(dismissalKey) === 'true');
      if (clearOnFirstCompletion && hasSeenActiveKey) {
        setHasSeenActive(localStorage.getItem(hasSeenActiveKey) === 'true');
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          'useOnboardingDismissal: localStorage operation failed:',
          e
        );
      }
      setIsDismissed(false);
      setHasSeenActive(false);
    }
    // Reset tracking refs
    hasTrackedShownRef.current = false;
    hasTrackedCompletedRef.current = false;
    onboardingStartTimeRef.current = null;
    // Mark state as synced with the current key
    syncedKeyRef.current = dismissalKey;
  }, [dismissalKey, hasSeenActiveKey, clearOnFirstCompletion]);

  // Reset lifecycle refs when a new import session starts (inactive -> active transition)
  // This ensures per-session analytics even without unmount/remount
  useEffect(() => {
    const wasActive = prevHasActiveImportsRef.current;
    const isActive = importStatus.hasActiveImports;
    prevHasActiveImportsRef.current = isActive;

    // New import session started: reset lifecycle tracking
    if (!wasActive && isActive) {
      hasTrackedShownRef.current = false;
      hasTrackedCompletedRef.current = false;
      onboardingStartTimeRef.current = null;
    }
  }, [importStatus.hasActiveImports]);

  // Track hasSeenActive when imports become active
  useEffect(() => {
    if (!clearOnFirstCompletion || !hasSeenActiveKey) {
      return;
    }
    if (importStatus.hasActiveImports && !hasSeenActive) {
      setHasSeenActive(true);
      try {
        localStorage.setItem(hasSeenActiveKey, 'true');
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            'useOnboardingDismissal: localStorage operation failed:',
            e
          );
        }
      }
    }
  }, [
    importStatus.hasActiveImports,
    hasSeenActive,
    hasSeenActiveKey,
    clearOnFirstCompletion,
  ]);

  // Clear dismissal when imports finish
  useEffect(() => {
    // Don't clear while loading or if imports are still active
    if (importStatus.isLoading || importStatus.hasActiveImports) {
      return;
    }

    // Don't clear if userId is not ready (prevents premature clearing before auth loads)
    if (userId === undefined) {
      return;
    }

    // Determine if we should clear based on the configuration mode.
    // Two strategies exist because the components have different UX needs:
    // - clearOnFirstCompletion=true: For dedicated onboarding panels (like AIActionsPanel)
    //   that should reset for the next import session, but only if user saw this one.
    // - clearOnFirstCompletion=false: For inline banners (like ProposalCenter) where we
    //   want to reset the dismissal so the user sees the banner on their next import.
    const shouldClear = clearOnFirstCompletion
      ? hasSeenActive // AIActionsPanel behavior: only clear if we've seen active
      : isDismissed; // ProposalCenter behavior: clear if dismissed

    if (shouldClear) {
      try {
        localStorage.removeItem(dismissalKey);
        if (hasSeenActiveKey) {
          localStorage.removeItem(hasSeenActiveKey);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            'useOnboardingDismissal: localStorage operation failed:',
            e
          );
        }
      }
      setIsDismissed(false);
      if (clearOnFirstCompletion) {
        setHasSeenActive(false);
      }
    }
  }, [
    importStatus.isLoading,
    importStatus.hasActiveImports,
    hasSeenActive,
    isDismissed,
    dismissalKey,
    hasSeenActiveKey,
    clearOnFirstCompletion,
    userId,
  ]);

  // Compute showOnboarding
  // Don't show onboarding if:
  // - userId is not ready (prevents "unknown" key usage)
  // - State is not synced with current key (prevents stale state during userId transition)
  const showOnboarding =
    userId !== undefined &&
    isStateSynced &&
    importStatus.hasActiveImports &&
    !isDismissed;

  // Track onboarding shown (lifecycle tracking)
  useEffect(() => {
    if (!trackFullLifecycle) {
      return;
    }
    if (showOnboarding && !hasTrackedShownRef.current) {
      hasTrackedShownRef.current = true;
      onboardingStartTimeRef.current = Date.now();
      // Emit one boolean per active provider so the existing
      // mixpanel dashboards built on gmailActive/gcalActive keep working,
      // and surface every other provider under its own key.  Legacy keys
      // are also emitted as explicit booleans so dashboards see `false`
      // (not `undefined`) when those providers are absent.
      const activeByProvider: Record<string, boolean> = {};
      for (const svc of importStatus.services) {
        activeByProvider[`${svc.providerName}Active`] = svc.active;
      }
      track('onboarding_import_shown', {
        orgId,
        gmailActive: activeByProvider.gmailActive ?? false,
        gcalActive: activeByProvider.gcalActive ?? false,
        ...activeByProvider,
      });
    }
  }, [showOnboarding, orgId, importStatus.services, trackFullLifecycle]);

  // Track onboarding completed (lifecycle tracking)
  useEffect(() => {
    if (!trackFullLifecycle) {
      return;
    }
    if (
      !importStatus.hasActiveImports &&
      !importStatus.isLoading &&
      onboardingStartTimeRef.current &&
      !hasTrackedCompletedRef.current
    ) {
      hasTrackedCompletedRef.current = true;
      const durationMs = Date.now() - onboardingStartTimeRef.current;
      track('onboarding_import_completed', { orgId, durationMs });
    }
  }, [
    importStatus.hasActiveImports,
    importStatus.isLoading,
    orgId,
    trackFullLifecycle,
  ]);

  /**
   * Dismiss the onboarding banner.
   *
   * Note: This callback is recreated when orgId, userId, or dismissalKey changes.
   * This is intentional - the callback uses the current org/user context.
   *
   * Progress tracking captures `combinedProgress` at the time of dismissal.
   * This reflects the user's perception of progress when they chose to dismiss.
   */
  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(dismissalKey, 'true');
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          'useOnboardingDismissal: localStorage operation failed:',
          e
        );
      }
    }
    setIsDismissed(true);

    track(dismissEventName, {
      orgId,
      progressAtDismissal: importStatus.combinedProgress,
    });

    onAfterDismiss?.();
  }, [
    dismissalKey,
    dismissEventName,
    orgId,
    importStatus.combinedProgress,
    onAfterDismiss,
  ]);

  return {
    showOnboarding,
    isDismissed,
    handleDismiss,
  };
}
